import { useMutation } from '@apollo/client';
import { parseQueryString, OptionalString } from '@sixfold/app-utils';
import { Button, Checkbox, Popover, Stack, useNotifications } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import { gql } from 'graphql-tag';
import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router';

import { ManageVehicleModal } from './manage_vehicle_modal';
import { ConfirmButton } from '../../components/confirm_button';
import { emptyTableState, getTableHeaderClassNames } from '../../components/table';
import {
  DeleteVehicleTrackerInput,
  MutationDeleteVehicleTrackerArgs,
  VehicleTrackerType,
  PAGINATION_PAGE_SIZE,
} from '../../lib/graphql';
import { Table } from '../../lib/table';
import { Routes } from '../../routes';
import { managedVehiclesQuery } from '../../vehicle/graphql';

export type ManagedVehicle = {
  tracker_id: string;
  vehicle_id: string;
  remote_id: string;
  license_plate: string;
};

const deleteVehicleTrackerMutation = gql`
  mutation DeleteVehicleTracker($input: DeleteVehicleTrackerInput!) {
    deleteVehicleTracker(input: $input)
  }
`;

type VehicleNode = {
  vehicle_id: string;
  license_plate_number: string | null;
  created_at: string | null;
  telematicsIntegrations: { telematics_integration_id: string }[];
  vehicleTrackers: (
    | ({
        vehicleTrackerId: string;
        trackerType: VehicleTrackerType;
        fmsRemoteId: string;
        telematicsIntegration: { telematicsIntegrationId: string } | null;
      } & { __typename: 'FMSVehicleTracker' })
    | ({ vehicleTrackerId: string; trackerType: VehicleTrackerType } & {
        __typename: 'MobileVehicleTracker';
      })
  )[];
};

export interface ManagedVehicleListProps {
  vehicles: VehicleNode[];
  telematicsIntegrationId: string;
  hasEditPermission: boolean;
}

interface SelectedVehiclesState {
  vehicles: Record<string, boolean | undefined>;
  isSelectAllEnabled: boolean;
}

class VehicleTable extends Table<ManagedVehicle> {}

export const ManagedVehicleList: React.FC<ManagedVehicleListProps> = ({
  vehicles,
  telematicsIntegrationId,
  hasEditPermission,
}) => {
  const [selectedVehicles, setSelectedVehicles] = useState<SelectedVehiclesState>({
    vehicles: {},
    isSelectAllEnabled: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredVehicles: ManagedVehicle[] = (
    vehicles.filter(notNil).map((v) => {
      const trackers = v.vehicleTrackers.filter(
        (tr) =>
          tr.__typename === 'FMSVehicleTracker' &&
          tr.telematicsIntegration?.telematicsIntegrationId === telematicsIntegrationId,
      );

      if (trackers.length !== 1) {
        return null;
      }
      const tracker: { fmsRemoteId: string; vehicleTrackerId: string } = trackers[0] as {
        fmsRemoteId: string;
        vehicleTrackerId: string;
      };

      return {
        remote_id: tracker.fmsRemoteId,
        license_plate: v.license_plate_number ?? '-',
        vehicle_id: v.vehicle_id ?? '-',
        tracker_id: tracker.vehicleTrackerId,
      };
    }) ?? []
  ).filter(notNil);

  const vehiclesToDelete = useMemo(
    () =>
      Object.entries(selectedVehicles.vehicles)
        .filter(([, value]) => value === true)
        .map(([key]) => key),
    [selectedVehicles.vehicles],
  );

  const vehiclesToDeleteCount = vehiclesToDelete.length;
  const isDeleteButtonDisabled = !hasEditPermission || vehiclesToDeleteCount === 0 || isDeleting;

  const onSelectAllVehicles = useCallback(() => {
    setSelectedVehicles(({ isSelectAllEnabled }) => {
      const vehiclesToSelect = filteredVehicles.reduce((memo, curr) => {
        return { ...memo, [curr.tracker_id]: !isSelectAllEnabled };
      }, {});

      return {
        vehicles: vehiclesToSelect,
        isSelectAllEnabled: !isSelectAllEnabled,
      };
    });
  }, [filteredVehicles]);

  const onSelectVehicle = useCallback((trackerId: string) => {
    setSelectedVehicles(({ vehicles }) => {
      const currentlySelectedVehicleState = vehicles[trackerId] ?? false;
      const newVehicles = { ...vehicles, [trackerId]: !currentlySelectedVehicleState };

      return {
        isSelectAllEnabled: false,
        vehicles: newVehicles,
      };
    });
  }, []);

  const notify = useNotifications();

  const queryString = parseQueryString({
    queryParametersToParse: {
      license_plate: OptionalString,
    },
    queryString: location.search,
  });

  const refetchConfig = useMemo(
    () => ({
      query: managedVehiclesQuery,
      variables: {
        integrationIds: [telematicsIntegrationId],
        limit: PAGINATION_PAGE_SIZE,
        licensePlateSearch: queryString.license_plate || undefined,
      },
    }),
    [telematicsIntegrationId, queryString.license_plate],
  );

  const [deleteVehicleTracker, { client }] = useMutation<DeleteVehicleTrackerInput, MutationDeleteVehicleTrackerArgs>(
    deleteVehicleTrackerMutation,
  );

  const handleBulkDelete = async (trackerIdsToDelete: string[]) => {
    setIsDeleting(true);
    try {
      const deletionPromises = trackerIdsToDelete.map((trackerId) =>
        deleteVehicleTracker({
          variables: {
            input: { trackerId },
          },
        }),
      );

      const results = await Promise.allSettled(deletionPromises);
      const failedDeletions = results.filter((result) => result.status === 'rejected');

      if (failedDeletions.length > 0) {
        notify.error({
          title: `${failedDeletions.length} of ${trackerIdsToDelete.length} deletions failed`,
        });
      } else {
        notify.success({
          title: `${trackerIdsToDelete.length} vehicle(s) disconnected from the FMS.`,
        });
      }

      if (client) {
        await client.query({
          query: managedVehiclesQuery,
          variables: {
            integrationIds: [telematicsIntegrationId],
            limit: PAGINATION_PAGE_SIZE,
            licensePlateSearch: queryString.license_plate || undefined,
          },
          fetchPolicy: 'network-only',
        });
      }
    } catch (_error) {
      notify.error({
        title: 'Failed to delete vehicles',
      });
    } finally {
      setIsDeleting(false);
      setSelectedVehicles({ isSelectAllEnabled: false, vehicles: {} });
    }
  };

  return (
    <>
      <ConfirmButton
        initialButtonColor="red"
        label={
          isDeleting
            ? 'Deleting...'
            : vehiclesToDeleteCount === 0
              ? 'Delete vehicles'
              : `Delete ${vehiclesToDeleteCount} vehicles`
        }
        className="mini"
        disabled={isDeleteButtonDisabled}
        onConfirm={() => handleBulkDelete(vehiclesToDelete)}
      />
      {isDeleteButtonDisabled && (
        <Popover
          placement="top"
          content={
            <>
              <strong>Deleting vehicles</strong>
              <ul>
                <li>At least one vehicle must be selected</li>
                <li>You must have edit permissions</li>
              </ul>
            </>
          }>
          <Button kind="ghost" size="xsmall" hideLabel iconStart="small-help">
            Deleting vehicles help
          </Button>
        </Popover>
      )}

      <VehicleTable
        className="ui-table compact-table"
        data={filteredVehicles}
        tableHeaders={{
          defaultClassName: getTableHeaderClassNames,
          columns: [
            {
              value: (
                <Checkbox
                  label=""
                  hideLabel
                  onChange={() => onSelectAllVehicles()}
                  checked={selectedVehicles.isSelectAllEnabled}
                  disabled={isDeleting}
                  aria-label="Select all vehicles"
                />
              ),
            },
            { keyPath: 'vehicle_id', value: 'Vehicle ID' },
            { keyPath: 'remote_id', value: 'Remote ID' },
            { keyPath: 'license_plate', value: 'License Plate' },
            { value: 'Actions' },
          ],
        }}
        defaultSortBy={{ keyPath: 'remote_id', value: 'ASC' }}
        emptyStatePlaceholder={emptyTableState('No vehicles')}>
        {({ row }) => {
          const { vehicle_id, remote_id, license_plate, tracker_id } = row.data;
          const onConfirmDelete = async () => {
            await deleteVehicleTracker({
              variables: {
                input: {
                  trackerId: tracker_id,
                },
              },
              refetchQueries: [refetchConfig],
            });
            notify.success({
              title:
                'Vehicle disconnected from the FMS. If this was the only tracker for the vehicle, then the vehicle was also deleted.',
            });
          };
          return (
            <tr key={vehicle_id}>
              <td>
                <Checkbox
                  label=""
                  hideLabel
                  checked={selectedVehicles.vehicles[tracker_id] ?? false}
                  onChange={() => onSelectVehicle(tracker_id)}
                  aria-label={`Select vehicle ${license_plate}`}
                  disabled={isDeleting}
                />
              </td>
              <td style={{ textAlign: 'right' }}>
                <Link to={Routes.Vehicle.generatePath({ vehicle_id })}>{vehicle_id}</Link>
              </td>
              <td>{remote_id}</td>
              <td>{license_plate}</td>
              <td>
                <Stack justifyContent="end">
                  <ManageVehicleModal
                    telematicsIntegrationId={telematicsIntegrationId}
                    trigger={<Button disabled={!hasEditPermission}>Edit</Button>}
                    existingVehicle={{ license_plate, remote_id, tracker_id }}
                  />
                  <ConfirmButton
                    size="tiny"
                    label="Disconnect"
                    initialButtonColor={'red'}
                    disabled={!hasEditPermission || isDeleting}
                    onConfirm={onConfirmDelete}
                    aria-label={`Disconnect vehicle ${license_plate}`}
                  />
                </Stack>
              </td>
            </tr>
          );
        }}
      </VehicleTable>
    </>
  );
};
